<template>
  <Button outlined severity="secondary" style="font-size: small;" @click="toggleDialog()">
    <span v-if="$slots.prefix" style="font-style: italic;">
      <slot name="prefix"></slot>
    </span>
    <span style="font-weight: 500;">
      {{ formatDate(dateToLabel, props.placeholderLabel) }}
    </span>
  </Button>
  <Dialog modal dismissableMask :showHeader="false" v-model:visible="showingDialog" style="padding: 15px;">
    <div style="display: flex; gap: 40px; align-items: flex-start; margin-top: 15px;">
      <div style="display: flex; flex-direction: column; gap: 10px;">
        <div style="height: 45px; display: flex; align-items: center; padding-left: 5px; gap: 15px;">
          <h3>From</h3>
          <div style="display: flex; flex-grow: 1; font-size: small; font-style: italic;">
            {{ formatDate(dateFrom, "Oldest record") }}
          </div>
          <Button v-if="dateFrom" text size="small" @click="dateFrom = null">
            Clear
          </Button>
        </div>
        <DatePicker :date="dateFrom" @update:date="setDateFrom_withTimeReset"></DatePicker>
      </div>
      <div style="display: flex; flex-direction: column; gap: 10px;">
        <div style="height: 45px; display: flex; align-items: center; padding-left: 5px; gap: 15px;">
          <h3>To</h3>
          <div style="display: flex; flex-grow: 1; font-size: small; font-style: italic;">
            {{ formatDate(dateTo, "Newest record") }}
          </div>
          <Button v-if="dateTo" text size="small" @click="dateTo = null">
            Clear
          </Button>
        </div>
        <DatePicker :date="dateTo" @update:date="setDateTo_withTimeReset"></DatePicker>
      </div>
    </div>
    <div style="display: flex; gap: 10px; margin-top: 40px;">
      <Button severity="secondary" @click="showAll()">
        Show all
      </Button>
      <Button severity="secondary" @click="toggleRangeMenu">
        Show last ...
        <i class="pi pi-angle-down"></i>
      </Button>
      <Menu ref="rangeMenu" popup :model="rangeMenuItems"></Menu>
      <div style="flex-grow: 1;"></div>
      <Button @click="ok()">
        Ok
      </Button>
      <Button severity="secondary" @click="cancel()">
        Cancel
      </Button>
    </div>
  </Dialog>
</template>

<script setup>
import { ref, readonly } from "vue";
import { padStart } from "./utility.js";
import { zoneDate } from "./timezones.js";
import DatePicker from "./DatePicker.vue";

let props = defineProps([
  "dateFrom",
  "dateTo",
  "dateToLabel",
  "placeholderLabel"
]);

let emit = defineEmits([
  "update:dateFrom",
  "update:dateTo"
]);

let dateFrom = ref(props.dateFrom);
let dateTo = ref(props.dateTo);

function setDateFrom_withTimeReset(newDate) {
  setDateWithTimeReset(dateFrom, newDate);
}

function setDateTo_withTimeReset(newDate) {
  setDateWithTimeReset(dateTo, newDate);
}

// Sets the selected date's time to midnight if the day changed.
function setDateWithTimeReset(dateRef, newDate) {
  let didDayChange = (
    dateRef.value?.getUTCFullYear() != newDate.getUTCFullYear() ||
    dateRef.value?.getUTCMonth() != newDate.getUTCMonth() ||
    dateRef.value?.getUTCDate() != newDate.getUTCDate()
  );
  if (didDayChange) {
    newDate.setUTCHours(0);
    newDate.setUTCMinutes(0);
  }
  dateRef.value = newDate;
}

let showingDialog = ref(false);
function toggleDialog() {
  showingDialog.value = true;
  dateFrom.value = props.dateFrom;
  dateTo.value = props.dateTo;
}

let rangeMenu = ref();
function toggleRangeMenu(event) {
  rangeMenu.value.toggle(event);
}

let rangeMenuItems = readonly([
  { label: "Hour", command: () => showLast(3600000) },
  { label: "Day", command: () => showLast(24 * 3600000) },
  { label: "Week", command: () => showLast(7 * 24 * 3600000) },
  { label: "Month", command: () => showLast(30 * 24 * 3600000) },
  { label: "Year", command: () => showLast(365 * 24 * 3600000) }
]);

function showAll() {
  emit("update:dateFrom", null);
  emit("update:dateTo", null);
  showingDialog.value = false;
}

function showLast(offset) {
  let system = store.sessions[0].system;
  let dateNow = zoneDate(system.utcTime.value * 1000, system.timezone.value);
  let dateFrom = new Date(dateNow.getTime() - offset);
  emit("update:dateFrom", dateFrom);
  emit("update:dateTo", dateNow);
  showingDialog.value = false;
}

function ok() {
  let swapDates = (dateFrom.value?.getTime() > dateTo.value?.getTime());
  if (swapDates) {
    emit("update:dateFrom", dateTo.value);
    emit("update:dateTo", dateFrom.value);
  }
  else {
    emit("update:dateFrom", dateFrom.value);
    emit("update:dateTo", dateTo.value);
  }
  showingDialog.value = false;
}

function cancel() {
  showingDialog.value = false;
}

function formatDate(date, nullLabel) {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  if (date == null)
    return nullLabel;

  let hours = date.getUTCHours();
  let hours12 = (hours) ? (hours <= 12 ? hours : hours - 12) : 12;
  let meridiem = (hours < 12) ? " AM" : " PM";
  let timePart = padStart(hours12, 2, "0") + ":" + padStart(date.getUTCMinutes(), 2, "0") + meridiem;
  let datePart = days[date.getUTCDay()] + " " + months[date.getUTCMonth()] + " " + date.getUTCDate() + ", " + date.getUTCFullYear();
  
  let isMidnight = (hours == 0 && date.getUTCMinutes() == 0);
  let shouldOmitTimePart = isMidnight;
  if (shouldOmitTimePart)
    return datePart;
  else
    return timePart + " " + datePart;
}
</script>

<style scoped>
.p-button-secondary {
  color: #333;
}
:deep(.dp__theme_light) {
  --dp-primary-color: var(--blue-400);
}
:deep(.dp__menu) {
  border: 0;
}
:deep(.dp__menu_inner) {
  padding: 0;
}
:deep(.dp__calendar) {
  margin-top: 10px;
}
:deep(.dp__today) {
  border: 0;
  font-weight: bold;
  text-decoration: underline;
}
:deep(.dp--tp-wrap) {
  margin-top: 15px;
}
:deep(.dp__pm_am_button) {
  color: #333;
  background: var(--gray-100);
}
</style>